import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import i18n from '@/locales/config';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App, ConfigProvider, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import React, { ReactNode, useEffect } from 'react';
import { ThemeProvider } from './components/theme-provider';
import { SidebarProvider } from './components/ui/sidebar';
import { TooltipProvider } from './components/ui/tooltip';
import { ThemeEnum } from './constants/common';
import { useWebsiteConfig } from './hooks/system-hooks';
import { brand } from './theme/brand';
import storage from './utils/authorization-util';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
// Ant Design 的中文文案不会自动切换 dayjs 的月份和星期显示语言。
dayjs.locale('zh-cn');

if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React, {
    trackAllPureComponents: true,
    trackExtraHooks: [],
    logOnDifferentValues: true,
  });
}

const queryClient = new QueryClient();

const chunkReloadKey = 'asi:chunk-reload-path';

function recoverFromStaleChunk(error: unknown) {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  if (
    !/ChunkLoadError|Loading chunk .+ failed|dynamically imported module/i.test(
      message,
    )
  ) {
    return;
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const now = Date.now();
  try {
    const previous = JSON.parse(
      window.sessionStorage.getItem(chunkReloadKey) || 'null',
    );
    if (
      previous?.path === currentPath &&
      Number.isFinite(previous?.at) &&
      now - previous.at < 60_000
    ) {
      return;
    }
  } catch {
    // Ignore an invalid value left by an older frontend build.
  }
  window.sessionStorage.setItem(
    chunkReloadKey,
    JSON.stringify({ path: currentPath, at: now }),
  );
  window.location.reload();
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) =>
    recoverFromStaleChunk(event.error || event.message),
  );
  window.addEventListener('unhandledrejection', (event) =>
    recoverFromStaleChunk(event.reason),
  );
}

function WebsiteDocumentTitle() {
  const { website } = useWebsiteConfig();

  useEffect(() => {
    document.title = [website.siteName, website.siteSubtitle]
      .filter(Boolean)
      .join(' ');
  }, [website.siteName, website.siteSubtitle]);

  return null;
}

function Root({ children }: React.PropsWithChildren) {
  return (
    <>
      <ConfigProvider
        button={{ autoInsertSpace: false }}
        theme={{
          token: {
            fontFamily: 'Inter, PingFang SC, Microsoft YaHei, sans-serif',
            colorPrimary: brand.blue,
            colorInfo: brand.blue,
            colorSuccess: brand.green,
            colorBgBase: '#FFFFFF',
            colorBgLayout: brand.page,
            colorTextBase: brand.ink,
            colorTextSecondary: brand.textSecondary,
            colorBorder: brand.border,
            borderRadius: 10,
            controlHeight: 40,
          },
          algorithm: theme.defaultAlgorithm,
        }}
        locale={zhCN}
      >
        <SidebarProvider className="h-full">
          <App>{children}</App>
        </SidebarProvider>
        <Sonner position={'top-right'} expand richColors closeButton></Sonner>
        <Toaster />
      </ConfigProvider>
    </>
  );
}

const RootProvider = ({ children }: React.PropsWithChildren) => {
  useEffect(() => {
    // Because the language is saved in the backend, a token is required to obtain the api. However, the login page cannot obtain the language through the getUserInfo api, so the language needs to be saved in localstorage.
    storage.setLanguage('zh');
    i18n.changeLanguage('zh');
  }, []);

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <WebsiteDocumentTitle />
        <ThemeProvider
          defaultTheme={ThemeEnum.Light}
          storageKey="ragflow-ui-theme"
        >
          <Root>{children}</Root>
        </ThemeProvider>
      </QueryClientProvider>
    </TooltipProvider>
  );
};
export function rootContainer(container: ReactNode) {
  return <RootProvider>{container}</RootProvider>;
}
